<template>
  <header class="pdv-header" :class="{ 'pdv-header--fechado': caixaHabilitado && !caixaAberto }">
    <!-- Esquerda: marca + terminal + conexão/pêndencias ─────────────── -->
    <div class="pdv-header__left">
      <div class="pdv-header__brand" :title="terminalUuid || ''">
        <img :src="brandLogo" :alt="brandName" class="pdv-header__logo" />
        <div class="pdv-header__brand-info">
          <span class="pdv-header__terminal-name">{{ terminalNome || 'PDV' }}</span>
          <span v-if="serie" class="pdv-header__serie">Série {{ serie }}</span>
        </div>
      </div>

      <span class="pdv-header__sep">|</span>

      <span
        :class="[
          'pdv-header__conexao',
          online ? 'pdv-header__conexao--online' : 'pdv-header__conexao--offline',
        ]"
        :title="online ? 'Conectado ao servidor' : 'Sem conexão — operando offline'"
      >
        <span class="pdv-header__dot"></span>
        {{ online ? 'Online' : 'Offline' }}
      </span>

      <span
        v-if="pendentesSync > 0"
        class="pdv-header__pendencias"
        :title="`${pendentesSync} movimentação(ões) de caixa pendente(s) de sincronização`"
      >
        <i class="fas fa-cloud-upload-alt"></i>
        Pendências ({{ pendentesSync }})
      </span>
    </div>

    <!-- Direita: operador / status do caixa / atalhos topo ─────────────── -->
    <div class="pdv-header__right">
      <button
        type="button"
        class="pdv-header__btn pdv-header__btn--ghost"
        @click="$emit('ir-dashboard')"
      >
        <i class="fas fa-home"></i> Dashboard
      </button>

      <div class="pdv-header__operador" :title="`Operador logado: ${operadorNome || '—'}`">
        <i class="fas fa-user-circle"></i>
        <div class="pdv-header__operador-info">
          <span class="pdv-header__operador-label">Operador</span>
          <span class="pdv-header__operador-nome">{{ operadorNome || '—' }}</span>
        </div>
      </div>

      <span v-if="caixaHabilitado" class="pdv-header__sep">|</span>

      <div v-if="caixaHabilitado" class="pdv-header__caixa-info">
        <span
          v-if="caixaAberto"
          class="pdv-header__caixa-badge pdv-header__caixa-badge--aberto"
        >
          <i class="fas fa-lock-open"></i> Caixa aberto
        </span>
        <span v-else class="pdv-header__caixa-badge pdv-header__caixa-badge--fechado">
          <i class="fas fa-lock"></i> Caixa fechado
        </span>

        <span v-if="caixaAberto" class="pdv-header__caixa-meta">
          desde {{ horaAbertura }}<template v-if="!fechamentoAsCegas"> · {{ formatBRL(totalVendido) }} vendido</template>
        </span>
      </div>

      <div v-if="caixaHabilitado" ref="caixaTrigger" class="pdv-header__caixa-trigger-wrap">
        <button
          v-if="!caixaAberto"
          type="button"
          class="pdv-header__btn pdv-header__btn--success"
          @click="$emit('abrir-caixa')"
        >
          <i class="fas fa-lock-open"></i> Abrir caixa
        </button>

        <button
          type="button"
          class="pdv-header__btn pdv-header__btn--caixa"
          :class="{ 'pdv-header__btn--ativo': dropdownAberto }"
          @click="toggleDropdown"
        >
          <i class="fas fa-cash-register"></i>
          Caixa
          <i
            class="fas pdv-header__caret"
            :class="dropdownAberto ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>

        <CaixaResumoDropdown
          v-if="dropdownAberto"
          :caixa-aberto="caixaAberto"
          :resumo="resumo"
          :operador-nome="operadorNome"
          :terminal-nome="terminalNome"
          :pendentes-sync="pendentesSync"
          :sincronizando="sincronizando"
          :online="online"
          :fechamento-as-cegas="fechamentoAsCegas"
          @abrir-caixa="onAcao('abrir-caixa')"
          @fechar-caixa="onAcao('fechar-caixa')"
          @sangria="onAcao('sangria')"
          @suprimento="onAcao('suprimento')"
          @resumo-completo="onAcao('resumo-completo')"
          @sincronizar="onAcao('sincronizar')"
          @recarregar-combinacoes="onAcao('recarregar-combinacoes')"
          @fechar="dropdownAberto = false"
        />
      </div>

      <button
        type="button"
        class="pdv-header__btn pdv-header__btn--icon"
        title="Configurações"
        @click="$emit('abrir-configuracoes')"
      >
        <i class="fas fa-cog"></i>
      </button>

      <button
        type="button"
        class="pdv-header__btn pdv-header__btn--ghost"
        @click="$emit('sair')"
      >
        <i class="fas fa-sign-out-alt"></i> Sair
      </button>
    </div>
  </header>
</template>

<script>
import CaixaResumoDropdown from "@/modules/PdvDesktop/components/CaixaResumoDropdown.vue";
import BrandService from "@/modules/PdvDesktop/services/BrandService";

export default {
  name: "PdvHeader",

  components: { CaixaResumoDropdown },

  props: {
    caixaAberto:   { type: Object,  default: null },
    resumo:        { type: Object,  default: null },
    online:        { type: Boolean, default: true },
    operadorNome:  { type: String,  default: "" },
    terminalNome:  { type: String,  default: "" },
    serie:         { type: [String, Number], default: "" },
    terminalUuid:  { type: String,  default: "" },
    pendentesSync: { type: Number,  default: 0 },
    sincronizando: { type: Boolean, default: false },
    caixaHabilitado: { type: Boolean, default: true },
    fechamentoAsCegas: { type: Boolean, default: false },
  },

  data() {
    return {
      brandLogo: BrandService.logo,
      brandName: BrandService.productName,
      dropdownAberto: false,
    };
  },

  created() {
    BrandService.carregar().then(() => { this.brandName = BrandService.productName; });
  },

  computed: {
    horaAbertura() {
      const iso = this.caixaAberto && this.caixaAberto.data_abertura;
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },

    totalVendido() {
      return (this.resumo && Number(this.resumo.total_vendas)) || 0;
    },
  },

  mounted() {
    document.addEventListener("mousedown", this._onClickFora);
    document.addEventListener("keydown",   this._onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this._onClickFora);
    document.removeEventListener("keydown",   this._onKeydown);
  },

  methods: {
    formatBRL(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },

    toggleDropdown() {
      this.dropdownAberto = !this.dropdownAberto;
    },

    onAcao(evt) {
      this.dropdownAberto = false;
      this.$emit(evt);
    },

    _onClickFora(e) {
      if (!this.dropdownAberto) return;
      const root = this.$refs.caixaTrigger;
      if (root && !root.contains(e.target)) {
        this.dropdownAberto = false;
      }
    },

    _onKeydown(e) {
      if (e.key === "Escape" && this.dropdownAberto) {
        this.dropdownAberto = false;
      }
    },
  },
};
</script>

<style scoped>
.pdv-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 54px;
  padding: 8px 14px;
  background: var(--primary-color, #ee7361);
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  font-size: 14px;
  color: #fff;
  flex-shrink: 0;
  position: relative;
  z-index: 50;
}

.pdv-header--fechado {
  background: linear-gradient(90deg, var(--primary-color, #ee7361) 0%, #e05a47 100%);
  border-bottom-color: rgba(255, 255, 255, 0.35);
}

.pdv-header i,
.pdv-header span,
.pdv-header div,
.pdv-header button {
  color: inherit;
}

.pdv-header__left,
.pdv-header__right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.pdv-header__sep {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 300;
}

/* ── Marca ────────────────────────────────────────────────────────────── */
.pdv-header__brand {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pdv-header__logo {
  height: 26px;
  width: auto;
  filter: brightness(0) invert(1);
}
.pdv-header__brand-info {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}
.pdv-header__serie {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

/* ── Terminal ─────────────────────────────────────────────────────────── */
.pdv-header__terminal {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
}
.pdv-header__terminal-label { color: rgba(255, 255, 255, 0.9); }
.pdv-header__terminal-name  { font-weight: 600; }

/* ── Conexão ──────────────────────────────────────────────────────────── */
.pdv-header__conexao {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
}
.pdv-header__conexao--online {
  background: #16a34a;
  color: #fff;
}
.pdv-header__conexao--offline {
  background: #dc2626;
  color: #fff;
}
.pdv-header__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

/* ── Pendências ───────────────────────────────────────────────────────── */
.pdv-header__pendencias {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* ── Operador ─────────────────────────────────────────────────────────── */
.pdv-header__operador {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
}
.pdv-header__operador > i { font-size: 17px; color: #fff; }
.pdv-header__operador-info {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}
.pdv-header__operador-label {
  font-size: 12px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.5px;
}
.pdv-header__operador-nome { font-weight: 700; }

/* ── Caixa info ───────────────────────────────────────────────────────── */
.pdv-header__caixa-info {
  display: flex;
  align-items: center;
  gap: 8px;
}
.pdv-header__caixa-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.pdv-header__caixa-badge--aberto {
  background: #16a34a;
  color: #fff;
}
.pdv-header__caixa-badge--fechado {
  background: #dc2626;
  color: #fff;
}
.pdv-header__caixa-meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

/* ── Botões ───────────────────────────────────────────────────────────── */
.pdv-header__caixa-trigger-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
}
.pdv-header__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  min-height: 36px;
  transition: background 0.15s, border-color 0.15s;
}
.pdv-header__btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.7);
}
.pdv-header__btn--success {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}
.pdv-header__btn--success:hover { background: #15803d; }
.pdv-header__btn--caixa {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;
}
.pdv-header__btn--caixa:hover { background: rgba(255, 255, 255, 0.24); }
.pdv-header__btn--ativo { background: rgba(255, 255, 255, 0.3); }
.pdv-header__caret { font-size: 12px; }

.pdv-header__btn--ghost {
  background: rgba(255, 255, 255, 0.1);
}

.pdv-header__btn--icon {
  width: 36px;
  height: 36px;
  padding: 0;
  justify-content: center;
}

@media (max-width: 1200px) {
  .pdv-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .pdv-header__left,
  .pdv-header__right {
    width: 100%;
  }

  .pdv-header__right {
    justify-content: space-between;
  }
}

@media (max-width: 760px) {
  .pdv-header__sep,
  .pdv-header__caixa-meta,
  .pdv-header__operador-info {
    display: none;
  }

  .pdv-header__btn {
    padding: 6px 10px;
    font-size: 12px;
  }

  .pdv-header__btn--icon {
    width: 34px;
    height: 34px;
    padding: 0;
  }

  .pdv-header__logo {
    height: 22px;
  }

  .pdv-header__pendencias {
    display: none;
  }
}
</style>
